import { isGradient } from "@/components/color-picker/lib/detect";
import { cn } from "@/lib/cn";
import { Tooltip } from "@/shared";
import { applyColorStyle } from "@/shared/utils/apply-color-style";
import {
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import { IconEdit, IconPlus, IconTrash } from "@tabler/icons-react";
import type { CSSProperties, MouseEvent as ReactMouseEvent } from "react";
import { forwardRef, useCallback, useState } from "react";

export const CHECKER_STYLE: CSSProperties = {
  backgroundImage:
    "linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)",
  backgroundSize: "8px 8px",
  backgroundPosition: "0 0, 0 4px, 4px -4px, -4px 0",
};

export const SWATCH_SHADOW =
  "0px 2px 1px rgba(0,0,0,0.01), 0px 1px 1px rgba(0,0,0,0.05), 0px 0px 1px rgba(0,0,0,0.09)";

/** primary-700, the selection ring color. */
const SWATCH_RING_COLOR = "#6306D1";

/**
 * Box-shadow for a swatch, including the selection ring when it is the applied
 * color.
 *
 * The ring is composed here rather than through Tailwind's `ring-*` utilities.
 * `ring-*` compiles to
 * `box-shadow: var(--tw-ring-offset-shadow), var(--tw-ring-shadow), var(--tw-shadow)`,
 * which every swatch then overwrote with an inline `box-shadow`, so the ring
 * resolved its variables and painted nothing. Routing the shadow through
 * `--tw-shadow` fixed that but handed the inactive state to Tailwind's ring
 * defaults, which drew a visible circle around every swatch. One explicit
 * shadow avoids both: nothing paints unless `isActive`, and it does not depend
 * on preflight defaults, on `ring-transparent` being generated, or on which
 * declaration wins.
 */
export const swatchShadow = (isActive?: boolean): CSSProperties => ({
  boxShadow: isActive
    ? `0 0 0 2px #fff, 0 0 0 4px ${SWATCH_RING_COLOR}, ${SWATCH_SHADOW}`
    : SWATCH_SHADOW,
});

export const BUTTON_SHADOW =
  "0px 2px 1px rgba(10,10,10,0.01), 0px 1px 1px rgba(10,10,10,0.03), 0px 0px 1px rgba(10,10,10,0.04)";

/**
 * Right-click context menu for color swatches. Presentational only — positioning,
 * portaling and dismiss are handled by the parent {@link ColorSwatch} via floating-ui.
 */
export const SwatchContextMenu = forwardRef<
  HTMLDivElement,
  {
    onEdit: () => void;
    onDelete: () => void;
    onClose: () => void;
    style?: CSSProperties;
    floatingProps?: Record<string, unknown>;
  }
>(({ onEdit, onDelete, onClose, style, floatingProps }, ref) => {
  return (
    <div
      ref={ref}
      style={{ ...style, width: 160 }}
      {...floatingProps}
      className="tw-z-[999999] tw-flex tw-flex-col tw-gap-0.5 tw-rounded-lg tw-bg-white tw-p-1 tw-shadow-sm tw-outline tw-outline-1 tw-outline-blue/5"
    >
      <button
        type="button"
        onClick={() => {
          onEdit();
          onClose();
        }}
        className="tw-flex tw-items-center tw-gap-1.5 tw-rounded tw-px-2 tw-py-2 tw-text-left tw-text-sm tw-font-normal tw-leading-5 tw-text-blue-base tw-transition-colors hover:tw-bg-blue-50"
      >
        <IconEdit className="tw-size-4 tw-shrink-0 tw-text-blue-base" />
        <span className="tw-flex-1">Edit</span>
      </button>
      <button
        type="button"
        onClick={() => {
          onDelete();
          onClose();
        }}
        className="tw-flex tw-items-center tw-gap-1.5 tw-rounded tw-px-2 tw-py-2 tw-text-left tw-text-sm tw-font-normal tw-leading-5 tw-text-blue-base tw-transition-colors hover:tw-bg-blue-50"
      >
        <IconTrash className="tw-size-4 tw-shrink-0 tw-text-blue-base" />
        <span className="tw-flex-1">Delete</span>
      </button>
    </div>
  );
});
SwatchContextMenu.displayName = "SwatchContextMenu";

/** Color swatch with checkerboard underlay, selection ring, and right-click context menu. */
export const ColorSwatch = ({
  color,
  isActive,
  onSelect,
  onEdit,
  onRemove,
  tooltipText,
}: {
  color: string;
  isActive: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onRemove: () => void;
  /** Tooltip text. Defaults to `Color: ${color}`. Ignored for gradients. */
  tooltipText?: string;
}) => {
  const [showContextMenu, setShowContextMenu] = useState(false);

  // floating-ui anchors the menu to the swatch and renders it in a portal, so it
  // escapes the color panel's `overflow` clipping and flips/shifts to stay on-screen.
  const { refs, floatingStyles, context } = useFloating({
    open: showContextMenu,
    onOpenChange: setShowContextMenu,
    placement: "bottom",
    strategy: "fixed",
    middleware: [offset(4), flip({ padding: 8 }), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });
  const dismiss = useDismiss(context, { outsidePressEvent: "mousedown" });
  const { getFloatingProps } = useInteractions([dismiss]);

  const handleContextMenu = useCallback((e: ReactMouseEvent) => {
    e.preventDefault();
    setShowContextMenu(true);
  }, []);

  const swatchEl = (
    <div className="tw-relative">
      <div
        className="tw-absolute tw-inset-0 tw-overflow-hidden tw-rounded-full"
        style={CHECKER_STYLE}
      />
      <div
        role="button"
        tabIndex={0}
        onClick={onSelect}
        onContextMenu={handleContextMenu}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") onSelect();
        }}
        style={{
          ...applyColorStyle(color, { target: "background" }),
          ...swatchShadow(isActive),
        }}
        className={cn(
          "tw-relative tw-h-8 tw-w-8 tw-cursor-pointer tw-rounded-full tw-border-none tw-outline tw-outline-1 -tw-outline-offset-1 tw-outline-blue-base/10 tw-transition-[box-shadow,transform] tw-duration-200 tw-ease-in-out hover:tw-scale-[1.0625]",
        )}
      />
    </div>
  );

  const tip = tooltipText ?? `Color: ${color}`;

  return (
    <div ref={refs.setReference} className="tw-relative">
      {isGradient(color) ? (
        swatchEl
      ) : (
        <Tooltip tooltip={tip} placement="top">
          {swatchEl}
        </Tooltip>
      )}
      {showContextMenu && (
        <FloatingPortal>
          <SwatchContextMenu
            ref={refs.setFloating}
            style={floatingStyles}
            floatingProps={getFloatingProps()}
            onEdit={onEdit}
            onDelete={onRemove}
            onClose={() => setShowContextMenu(false)}
          />
        </FloatingPortal>
      )}
    </div>
  );
};

/** "+" button to add a new color or gradient. */
export const AddColorButton = ({
  onClick,
  tooltip,
}: {
  onClick: () => void;
  /** Tooltip text. When omitted, no tooltip is rendered. */
  tooltip?: string;
}) => {
  const button = (
    <div
      role="button"
      tabIndex={0}
      aria-label={tooltip}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onClick();
      }}
      style={{ boxShadow: BUTTON_SHADOW }}
      className="tw-flex tw-h-8 tw-w-8 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-none tw-bg-white tw-outline tw-outline-1 -tw-outline-offset-1 tw-outline-blue-base/10 tw-transition-colors tw-duration-200 tw-ease-in-out hover:tw-bg-blue-50"
    >
      <IconPlus className="tw-size-4 tw-text-[#12033E]" />
    </div>
  );

  if (!tooltip) return button;

  return (
    <Tooltip tooltip={tooltip} placement="top">
      {button}
    </Tooltip>
  );
};
